import {
  BackgroundCheckStep,
  BackgroundCheckStepStatus,
  BackgroundCheckStepType,
  CropCashFlow,
  CropCashFlowDataItem
} from '@indigo-ag/schema';
import i18next from 'i18next';
import React from 'react';
import { useParams } from 'react-router-dom';
import { LoadingWrapper } from 'shared';
import styled from 'styled-components';

import { CreditApprovalHistory } from 'features/brazil-grower/components/DecisionSheet/CreditApprovalHistory';
import { CreditRiskAssessmentCard } from 'features/brazil-grower/components/DecisionSheet/CreditRiskAssessmentCard';
import { CropCashFlowList } from 'features/brazil-grower/components/DecisionSheet/CropCashFlowList';
import { opportunityContactCreditOverviewData } from 'features/brazil-grower/queries/creditApproval.queries';

const CreditOverviewCardsContainer = styled.div`
  > div {
    margin-top: 16px;
  }
`;

interface CreditOverviewProps {
  taxIdentifier: string | undefined;
}

export const CreditOverview = ({ taxIdentifier }: CreditOverviewProps) => {
  const { opportunityId, contactId } = useParams<{ contactId: string; opportunityId: string }>();

  return (
    <LoadingWrapper
      query={opportunityContactCreditOverviewData}
      queryOptions={{
        variables: {
          opportunityId,
          pagination: {
            first: 20
          },
          filters: { query: taxIdentifier },
          primaryContactId: contactId
        },
        skip: !taxIdentifier
      }}
    >
      {({ data }) => {
        if (data === undefined) {
          return i18next.t('error');
        }

        const {
          managerName,
          ownerName,
          backgroundCheck,
          primaryContact,
          spouseName,
          guarantorName,
          coIssuerName,
          currentRating,
          growerAge,
          yearsIndigoRelationship,
          yearsPlanting
        } = data.creditRiskAssessmentData ?? {};
        const { adminArea, state, city, locality } = primaryContact?.address ?? {};
        const stepTypeToStatus =
          backgroundCheck?.steps.reduce(
            (acc: Record<BackgroundCheckStepType, BackgroundCheckStepStatus>, step: BackgroundCheckStep) => ({
              ...acc,
              [step.type]: step.status
            }),
            {}
          ) ?? {};

        const creditApprovalHistory = data?.financingAgFinancingQueue;
        const items = creditApprovalHistory?.edges ?? [];
        const priorLoans = [];
        for (const item of items) {
          const {
            node: { campaign, collateralType, orderInfo, phase2 }
          } = item;
          for (const opportunity of orderInfo?.opportunities ?? []) {
            // This should be phase3.status, but the current API doesn't provide phase3 in the response.
            // Using phase2 for now since it is an improvement over not checking any phases.
            // TODO: https://symbiota.atlassian.net/browse/MERCH-1842 to use a better query
            // which will filter the data automatically.
            if (opportunity.id === undefined || phase2?.status !== 'APPROVED') continue;

            priorLoans.push({
              campaignName: campaign?.name,
              collateralType,
              volume: opportunity.collateralBags,
              tradingCompany: opportunity.tradingName,
              usedLimit: opportunity.contractValue
            });
          }
        }

        const cropCashFlows = data?.cropCashFlowData?.cropCashFlows?.map(
          ({
            crop,
            grossRevenue,
            indigoProductivity,
            leaseCost,
            leasedLand,
            ownedLand,
            percentOwnedLand,
            productionCost,
            totalLand
          }: CropCashFlowDataItem): CropCashFlow => ({
            crop,
            grossRevenue,
            indigoProductivity,
            leaseCost: leaseCost?.value,
            leasedLand,
            ownedLand,
            percentOwnedLand,
            productionCost: productionCost?.value,
            totalLand
          })
        );

        return (
          <CreditOverviewCardsContainer>
            <CreditRiskAssessmentCard
              isGrowerProfile
              backgroundCheckEmbargoStatus={stepTypeToStatus[BackgroundCheckStepType.EMBARGO]}
              backgroundCheckForcedLaborStatus={stepTypeToStatus[BackgroundCheckStepType.FORCED_LABOR]}
              backgroundCheckNegativeDebtStatus={stepTypeToStatus[BackgroundCheckStepType.NEGATIVE_DEBT]}
              coIssuerName={coIssuerName}
              contactAge={growerAge}
              contactCity={adminArea ?? city}
              contactName={primaryContact?.name}
              contactState={locality ?? state}
              currentRating={currentRating}
              guarantorName={guarantorName}
              salesManager={managerName}
              salesRep={ownerName}
              spouseName={spouseName}
              yearsExperiencePlanting={yearsPlanting}
              yearsWithIndigo={yearsIndigoRelationship}
            />
            <CreditApprovalHistory priorLoans={priorLoans} />
            <CropCashFlowList cropCashFlows={cropCashFlows} />
          </CreditOverviewCardsContainer>
        );
      }}
    </LoadingWrapper>
  );
};
